import api from './api';

const buildCountryFormData = (countryData = {}) => {
  const formData = new FormData();
  if (countryData.name !== undefined) formData.append('name', countryData.name);
  if (countryData.code !== undefined) formData.append('code', countryData.code);
  if (countryData.agreementAmount !== undefined) formData.append('agreementAmount', countryData.agreementAmount);
  if (countryData.isActive !== undefined) formData.append('isActive', String(countryData.isActive));
  if (countryData.agreementFile) formData.append('document', countryData.agreementFile);
  return formData;
};

const buildJobRoleFormData = (data = {}) => {
  const formData = new FormData();
  if (data.title !== undefined) formData.append('title', data.title);
  if (data.description !== undefined) formData.append('description', data.description);
  if (data.countryId !== undefined) formData.append('countryId', data.countryId);
  if (data.agreementAmount !== undefined) formData.append('agreementAmount', data.agreementAmount);
  if (data.isActive !== undefined) formData.append('isActive', String(data.isActive));
  if (data.agreementFile) formData.append('document', data.agreementFile);
  return formData;
};

/**
 * Job Management Service
 * Handles countries, job roles, and questionnaires
 */

// Country Services
export const countryService = {
  /**
   * Get all countries
   */
  getAll: async (includeInactive = false) => {
    const response = await api.get(`/countries?includeInactive=${includeInactive}`);
    return response.data;
  },

  /**
   * Get country by ID
   */
  getById: async (id) => {
    const response = await api.get(`/countries/${id}`);
    return response.data;
  },

  /**
   * Create country (Admin only)
   */
  create: async (countryData) => {
    const response = await api.post('/countries/admin', buildCountryFormData(countryData), {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Update country (Admin only)
   */
  update: async (id, countryData) => {
    const response = await api.put(`/countries/admin/${id}`, buildCountryFormData(countryData), {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Delete country (Admin only)
   */
  delete: async (id) => {
    const response = await api.delete(`/countries/admin/${id}`);
    return response.data;
  }
};

// Job Role Services
export const jobRoleService = {
  /**
   * Get job roles by country
   */
  getByCountry: async (countryId, includeInactive = false) => {
    const response = await api.get(`/job-roles?countryId=${countryId}&includeInactive=${includeInactive}`);
    return response.data;
  },

  /**
   * Get all job roles (Admin only)
   */
  getAll: async (includeInactive = false) => {
    const response = await api.get(`/job-roles/admin?includeInactive=${includeInactive}`);
    return response.data;
  },

  /**
   * Get job role by ID
   */
  getById: async (id) => {
    const response = await api.get(`/job-roles/${id}`);
    return response.data;
  },

  /**
   * Create job role (Admin only)
   */
  create: async (jobRoleData) => {
    const response = await api.post('/job-roles/admin', buildJobRoleFormData(jobRoleData), {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Update job role (Admin only)
   */
  update: async (id, jobRoleData) => {
    const response = await api.put(`/job-roles/admin/${id}`, buildJobRoleFormData(jobRoleData), {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Delete job role (Admin only)
   */
  delete: async (id) => {
    const response = await api.delete(`/job-roles/admin/${id}`);
    return response.data;
  }
};

// Questionnaire Services
export const questionnaireService = {
  /**
   * Get questionnaire by job role ID
   */
  getByJobRole: async (jobRoleId) => {
    const response = await api.get(`/questionnaires/${jobRoleId}`);
    return response.data;
  },

  /**
   * Get all questionnaires (Admin only)
   */
  getAll: async (includeInactive = false) => {
    const response = await api.get(`/admin/questionnaires?includeInactive=${includeInactive}`);
    return response.data;
  },

  /**
   * Get questionnaire by ID (Admin only)
   */
  getById: async (id) => {
    const response = await api.get(`/admin/questionnaires/${id}`);
    return response.data;
  },

  /**
   * Create questionnaire (Admin only)
   */
  create: async (questionnaireData) => {
    const response = await api.post('/admin/questionnaires', questionnaireData);
    return response.data;
  },

  /**
   * Update questionnaire (Admin only)
   */
  update: async (id, questionnaireData) => {
    const response = await api.put(`/admin/questionnaires/${id}`, questionnaireData);
    return response.data;
  },

  /**
   * Delete questionnaire (Admin only)
   */
  delete: async (id) => {
    const response = await api.delete(`/admin/questionnaires/${id}`);
    return response.data;
  }
};

export default {
  countryService,
  jobRoleService,
  questionnaireService
};